import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {EndpointsService} from "../endpoints.service";
import {group} from "@angular/animations";
import {GritApiService} from "./grit.api.service";
import { BehaviorSubject, Subscription, filter, interval, take, takeWhile } from 'rxjs';
import {environment} from "../../../environments/environment";

export enum GritUserRoleGroup {
  FullUser = "FullUser", // SecurityEntity = "SecurityEntity",  ANSP = "ANSP",
  ApprovingEntity = "ApprovingEntity", // ApprovingAuthority = "ApprovingAuthority",
  ReducedUser = "ReducedUser",
  Admin = "Admin",
}

@Injectable({
  providedIn: 'root'
})
export class GritUserService {

  static EMAIL_REGEX = /(?:[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)])/gi
  static validateEmail(email:string){return new RegExp(GritUserService.EMAIL_REGEX).test(email)}

  username: string;
  displayName: string;
  email: string;
  roles: string[];
  groupsPaths: string[];
  groupPathsAvailable: string[] = ["/"];

  profile : UserProfile = null;

  isAdmin$ : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private endpointsService: EndpointsService, private gritApiService: GritApiService, private http: HttpClient) {
    const checkAuthSubscription: Subscription = interval(100) // ms
      .pipe(takeWhile(() => !this.endpointsService.httpOptions, true))
      .subscribe(async () => {
        if (this.endpointsService.httpOptions) {
          checkAuthSubscription.unsubscribe();

          if(environment.useB2cAuthentication || environment.useKeycloakAuthentication) {
            let token = await this.endpointsService.setUserRolesToken();
            if (!token) {
              return;
            }
            let claims64 = token.split(".")[1];
            claims64 = claims64.replace(/-/g, "+").replace(/_/g, "/");
            const claims = JSON.parse(atob(claims64));
            this.roles = claims.ROLES ?? [];
            this.groupsPaths = claims.GROUPS ?? [];
            this.username = claims.sub;
          }else {
            let token = this.endpointsService.getAuthorizationToken();
            let claims64 = token.split(".")[1];
            claims64 = claims64.replace(/-/g, "+").replace(/_/g, "/");
            const claims = JSON.parse(atob(claims64))

            this.roles = claims.ROLES ?? [];
            this.groupsPaths = claims.GROUPS ?? [];
            this.username = claims.sub;

          }

          //All available groups
          for (let group of this.groupsPaths) {
            let domains = group.substring(1, group.length - 1).split("/");

            let path = "/"
            domains.forEach(domain => {
              path += `${domain}/`;
              if (!this.groupPathsAvailable.includes(path)) this.groupPathsAvailable.push(path);
            })
          }

          this.getProfile();
          this.getUserApprovalRequestInfo();
          this.isAdmin$.next(this.roles.some(role => role == GritUserRoleGroup.Admin));
        }
      });
  }

  private getUserApprovalRequestInfo(): void {
    this.http.get<any>(
      this.endpointsService.getUserProfile,
      this.endpointsService.httpOptions
    ).subscribe({
      next: (data) => {
        this.displayName = this.getDisplayNameFromApprovalRequest(data?.userApprovalRequestStr) || data?.userName || this.username;
        this.email = data?.email;
      },
      error: () => {
        this.displayName = this.username;
      }
    });
  }

  private getDisplayNameFromApprovalRequest(userApprovalRequestStr: string): string {
    if (!userApprovalRequestStr) return null;

    try {
      const info = JSON.parse(userApprovalRequestStr);
      const fullName = [info.firstName, info.lastName].filter(Boolean).join(' ').trim();
      return info.displayName || fullName || info.username || null;
    } catch {
      return null;
    }
  }

  private async getProfile(){
    this.profile = await this.gritApiService.userProfileCurrent() //TODO
  }

  public async updateProfileFirsSubscribed(){
    this.profile = await this.gritApiService.userProfileUpdateFirsSubscribed(JSON.stringify(this.profile.firsSubscribed));
  }


  public async updateProfileMailingList(){
    this.profile = await this.gritApiService.userProfileUpdateMailingList(JSON.stringify(this.profile.mailingList));
  }

  public logout() {
    document.cookie = "AuthToken" + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    location.reload();
  }


  public async isAdmin() : Promise<boolean> {
    while (!this.roles) {
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for roles to be available
    }
    return this.roles.some(role => role == GritUserRoleGroup.Admin);
  }
/*
  public async allowedRoles(...roles: string[]): Promise<boolean> {
    while (!this.roles) {
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for roles to be available
    }
    return this.roles.some(role => roles.includes(role));
  }*/
   public allowedRoles(...roles: string[]): boolean {
    if (!this.roles) return false;
    if (this.roles.includes(GritUserRoleGroup.Admin)) return true;
    return this.roles.some(role => roles.includes(role));
  }

  public getRoleGroupsPaths(roleGroup :GritUserRoleGroup) : string[]{
    let regex = new RegExp(`^\/.+\/${roleGroup}\/.+\/$`)
    return this.groupsPaths.filter(group => regex.test(group));
  }

  public allowedGroups (...groups: string[]) : boolean{
    return groups.some(group => this.groupsPaths.some(groupPath => groupPath.startsWith(group)));
  }

  public isOriginator (group : string) : boolean{
    return this.groupsPaths.includes(group);
  }
}

export class UserProfile {

  mailingList: MailingList[] = [];

  firsSubscribed : string[] = [];

  toDTO() : any{
    return {
      mailingList : JSON.stringify(this.mailingList),
      firsSubscribed : JSON.stringify(this.firsSubscribed),
    }
  }

  fromDTO(userProfileDto : any) : UserProfile{
    this.mailingList = JSON.parse(userProfileDto.mailingList ?? "[]");
    this.firsSubscribed = JSON.parse(userProfileDto.firsSubscribed ?? "[]");

    return this;
  }
}

export class MailingList {
  name : string = "Empty";
  list : {name:string; email: string}[] = [];

  constructor(name: string) {
    this.name = name;
  }
}
